import { User } from '../types';
import { ProductType } from '../types/warning.types';

const DERIVED_ENROLLMENT_PREFIX = 'derived-';

/**
 * Derived enrollments (`enrollmentId` starting with "derived-") are synthetic —
 * granted by the backend for CONVERSAS annual-plan students without a real
 * UserCourse record. Never a valid UUID; never usable in admin operations.
 */
export function isDerivedEnrollment(enrollmentId: string): boolean {
  return enrollmentId.startsWith(DERIVED_ENROLLMENT_PREFIX);
}

/** Returns the enrollmentId only if it's real (usable in admin operations), otherwise null. */
export function getAdminEnrollmentId(enrollmentId: string): string | null {
  return isDerivedEnrollment(enrollmentId) ? null : enrollmentId;
}

/** Case-insensitive to handle "ADMIN", "admin", "SUPER_ADMIN", "super_admin". */
export function isAdminUser(user: User | null): boolean {
  if (!user) return false;
  const role = user.role?.toLowerCase();
  return role === 'admin' || role === 'super_admin';
}

/**
 * Looks up the enrollment for `courseId` in user.courses and reads the flag
 * the backend computed (enrollment status + course status matrix).
 * Admins bypass the gate entirely.
 */
export function userCanAccessCommunity(user: User | null, courseId: string): boolean {
  if (!user) return false;
  if (isAdminUser(user)) return true;
  const course = user.courses?.find((c) => c.id === courseId);
  if (!course) return false;
  return course.canAccessCommunity === true;
}

export function userCanAccessLessons(user: User | null, courseId: string): boolean {
  if (!user) return false;
  if (isAdminUser(user)) return true;
  const course = user.courses?.find((c) => c.id === courseId);
  if (!course) return false;
  return course.canAccessLessons === true;
}

/** Cursos do tipo COMMUNITY não têm estrutura de capítulos: a aba Aulas mostra uma playlist única com todos os vídeos. */
export function isCommunityCourseType(user: User | null, courseId: string): boolean {
  const course = user?.courses?.find((c) => c.id === courseId);
  return course?.type === 'COMMUNITY';
}

/** Comentários nas aulas são exclusivos do produto CONVERSAS. */
export function isConversasCourseType(user: User | null, courseId: string): boolean {
  const course = user?.courses?.find((c) => c.id === courseId);
  return course?.type === 'CONVERSAS';
}

/** Deriva o ProductType do curso a partir de `course.type` — cursos sem `type` (não-COMMUNITY/CONVERSAS) são CLUB. */
export function getCourseProductType(user: User | null, courseId: string): ProductType | undefined {
  const course = user?.courses?.find((c) => c.id === courseId);
  if (!course) return undefined;
  return course.type === 'CONVERSAS' || course.type === 'COMMUNITY' ? course.type : 'CLUB';
}
